/**
 * My Quotations Page
 *
 * Issue #131: dedicated inbox for the Líder role. Lists every quotation
 * where the current user is the assigned `lider_id`. For admins it falls
 * back to "quotations I created" with an explanatory banner.
 *
 * Reuses the visual language from QuotationList (status badges, card
 * footer) so a technician switching between the two views feels at home.
 *
 * @module pages/quotations/MyQuotations
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
} from 'react-bootstrap';

import { listMyQuotations } from '../../services/quotationService';
import { formatDate } from '../../utils/formatters';
// #700 — el total con su rótulo cuando todavía no es un precio.
import QuotationTotal from '../../components/quotations/QuotationTotal';
import { margenSinDefinir } from '../../utils/calculations';
import { QUOTATION_STATUS } from '../../utils/constants';
import { useAuth } from '../../contexts/AuthContext';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { DataTable } from '../../components/common/DataTable';
import './QuotationList.css';

/**
 * Map a quotation status to its display metadata. Kept local to avoid
 * cross-component coupling — same shape as QuotationList.
 */
const formatQuotationStatus = (status) => {
  const statusMap = {
    [QUOTATION_STATUS.DRAFT]: { label: 'Borrador', variant: 'secondary', icon: 'bi-pencil' },
    [QUOTATION_STATUS.UNDER_REVIEW]: { label: 'En Revisión', variant: 'warning', icon: 'bi-hourglass' },
    [QUOTATION_STATUS.APPROVED]: { label: 'Aprobada', variant: 'success', icon: 'bi-check-circle' },
    [QUOTATION_STATUS.REJECTED]: { label: 'Rechazada', variant: 'danger', icon: 'bi-x-circle' },
    [QUOTATION_STATUS.SENT]: { label: 'Enviada', variant: 'info', icon: 'bi-send' },
    accepted: { label: 'Aceptada', variant: 'success', icon: 'bi-check-circle-fill' },
  };
  return statusMap[status] || { label: status, variant: 'secondary', icon: 'bi-question-circle' };
};

const MyQuotations = () => {
  const navigate = useNavigate();
  const { isAdmin, isTechnician } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();

  // Admins viewing this page see the quotations they created — they're
  // not in the Líder workflow themselves. Technicians see their assigned
  // quotations (the canonical Líder inbox).
  const asCreator = isAdmin && !isTechnician;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['my-quotations', { asCreator }],
    queryFn: () => listMyQuotations({ asCreator }),
    staleTime: 1000 * 60 * 5,
  });

  const quotations = data?.data || [];
  const totalCount = data?.count || 0;

  const handleRowClick = (quotationId) => {
    navigate(`/cotizaciones/${quotationId}/editar`);
  };

  // Build table columns once. Hooks must run before any early return.
  const columns = useMemo(
    () => [
      {
        id: 'project',
        header: 'Proyecto',
        accessor: (q) => q.project?.project_number || '',
        minWidth: 180,
        cell: (q) => (
          <div>
            <strong>{q.project?.project_number || '—'}</strong>
            <div className="small text-muted">{q.project?.title || ''}</div>
          </div>
        ),
      },
      {
        id: 'quotation_number',
        header: 'Cotización',
        accessor: (q) => q.quotation_number || '',
        minWidth: 130,
        cell: (q) => q.quotation_number || '—',
      },
      {
        id: 'status',
        header: 'Estado',
        accessor: 'status',
        minWidth: 130,
        cell: (q) => {
          const s = formatQuotationStatus(q.status);
          return (
            <Badge bg={s.variant}>
              <i className={`bi ${s.icon} me-1`}></i>
              {s.label}
            </Badge>
          );
        },
      },
      {
        id: 'supervisor',
        header: 'Supervisor',
        accessor: (q) => q.supervisor?.full_name || '',
        minWidth: 150,
        cell: (q) => q.supervisor?.full_name || '—',
      },
      {
        id: 'total',
        header: 'Total',
        accessor: (q) => q.total || 0,
        align: 'right',
        minWidth: 110,
        // #700 — `listMyQuotations` pide `*`, así que la fila SÍ trae los dos
        // porcentajes y el rótulo se puede MEDIR en vez de suponer.
        cell: (q) => <QuotationTotal quotation={q} marginUndefined={margenSinDefinir(q)} bold />,
      },
      {
        id: 'created_at',
        header: 'Fecha',
        accessor: 'created_at',
        minWidth: 110,
        cell: (q) => formatDate(q.created_at),
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '60vh' }}
        data-testid="my-quotations-loading"
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando cotizaciones...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error al cargar cotizaciones</Alert.Heading>
          <p>{error.message || 'No fue posible cargar tus cotizaciones.'}</p>
          <Button variant="outline-danger" onClick={() => refetch()}>
            Reintentar
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <DashboardLayout>
    <Container fluid className="py-4 quotation-list-view" data-testid="my-quotations-view">
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center quotation-list-header">
            <div className="mb-3 mb-md-0">
              <h2 className="mb-1">Mis Cotizaciones</h2>
              <p className="text-muted mb-0">
                {totalCount === 0
                  ? 'Sin cotizaciones asignadas'
                  : totalCount === 1
                  ? '1 cotización asignada'
                  : `${totalCount} cotizaciones asignadas`}
              </p>
            </div>
            <div className="d-flex gap-2 quotation-list-header-actions">
              <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
            </div>
          </div>
        </Col>
      </Row>

      {asCreator && (
        <Alert variant="warning" data-testid="my-quotations-admin-warning">
          <i className="bi bi-info-circle me-2"></i>
          Estás viendo todas tus cotizaciones <strong>como creador</strong>. Para el inbox del Líder,
          ingresá como técnico.
        </Alert>
      )}

      <Row>
        <Col>
          <Card>
            <Card.Body className="p-0">
              {quotations.length === 0 ? (
                <div className="text-center py-5" data-testid="my-quotations-empty">
                  <i
                    className="bi bi-inbox"
                    style={{ fontSize: '3rem', color: '#6c757d' }}
                  ></i>
                  <h5 className="mt-3 text-muted">No tenés cotizaciones asignadas</h5>
                  <p className="text-muted mb-0 px-3">
                    Cuando un Supervisor te asigne como Líder, aparecerán acá.
                  </p>
                </div>
              ) : isTableView ? (
                <DataTable
                  tableId="my-quotations"
                  data={quotations}
                  rowKey={(q) => q.id}
                  onRowClick={(q) => handleRowClick(q.id)}
                  columns={columns}
                />
              ) : (
                <Row className="g-3 p-3" data-testid="my-quotations-cards">
                  {quotations.map((quotation) => {
                    const statusInfo = formatQuotationStatus(quotation.status);
                    return (
                      <Col xs={12} sm={6} lg={4} key={quotation.id}>
                        <Card
                          className={`quotation-card quotation-card-${quotation.status}`}
                          onClick={() => handleRowClick(quotation.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleRowClick(quotation.id);
                            }
                          }}
                          data-testid="my-quotations-card"
                        >
                          <Card.Body className="py-3">
                            <div className="quotation-card-header">
                              <div>
                                <div className="quotation-card-project">
                                  {quotation.project?.project_number || 'Sin proyecto'}
                                </div>
                                <div className="quotation-card-project-title">
                                  {quotation.project?.title || '—'}
                                </div>
                              </div>
                              <Badge bg={statusInfo.variant}>
                                <i className={`bi ${statusInfo.icon} me-1`}></i>
                                {statusInfo.label}
                              </Badge>
                            </div>

                            <div className="quotation-card-body">
                              <div className="quotation-card-row">
                                <span className="quotation-card-label">Cotización</span>
                                <span className="quotation-card-value">
                                  {quotation.quotation_number || '—'}
                                </span>
                              </div>
                              <div className="quotation-card-row">
                                <span className="quotation-card-label">Supervisor</span>
                                <span className="quotation-card-value">
                                  {quotation.supervisor?.full_name || '—'}
                                </span>
                              </div>
                              <div className="quotation-card-row">
                                <span className="quotation-card-label">Total</span>
                                <span className="quotation-card-total">
                                  <QuotationTotal
                                    quotation={quotation}
                                    marginUndefined={margenSinDefinir(quotation)}
                                  />
                                </span>
                              </div>
                            </div>

                            <div className="quotation-card-footer">
                              <span>
                                <i className="bi bi-calendar me-1"></i>
                                {formatDate(quotation.created_at)}
                              </span>
                              <span className="text-primary">
                                <i className="bi bi-pencil-square me-1"></i>
                                Editar
                              </span>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    </DashboardLayout>
  );
};

export default MyQuotations;
